import { ImagePickerValue } from 'components/ImagePicker'
import { CouponPoolGenerationType, CouponPoolType } from 'models/CouponPool'
import { RedeemProductIds } from 'models/RedeemTemplate'
import { ValidationTypes } from 'models/Validation'
import { createContext, useContext } from 'react'
import {
  InstoreRedemptionType,
  RedeemTypeItem,
} from 'scenes/RedeemManager/types'

export type MultiUseRewardsSettings = {
  usageType: 'exactly_once' | 'multiple_times'
  limitType?: 'set_number' | 'unlimited'
  maxIssuances?: number
  activeWindowDuration?: number
  maxIssuancesPerWindow?: number
  windowDuration?: number
}

export type DiscountScope =
  | 'base_item'
  | 'base_item_plus_all_modifiers'
  | 'base_item_plus_required_modifiers'
  | 'required_modifiers'
  | 'free_base_item'
  | 'free_base_item_plus_all_modifiers'
  | 'free_base_item_plus_required_modifiers'
  | 'base_item_plus_discounted_modifiers'
  | 'free_base_item_plus_discounted_modifiers'
  | 'delivery_fee'

export type DeliveryFeeMode = 'amount' | 'percent' | 'fixed_price'

export type FormModel = {
  typeItem: RedeemTypeItem | null
  name: string
  description: string
  discount: string | null
  // null = disabled; '' / numeric = enabled.
  minimum: string | null
  maximum: string | null
  priceCap: string | null
  discountScope: DiscountScope
  deliveryFeeMode?: DeliveryFeeMode
  discountedCheckSection?: 'basket' | 'delivery_fee'
  perceivedValue: string
  availableOnline: boolean
  availableInstore: boolean
  discountedProductIds: string[]
  requiredProductIds: string[]
  discountedModifierIds: string[]
  productIds?: RedeemProductIds
  instoreDiscountedProductIds: string[]
  instoreRequiredProductIds: string[]
  instoreDiscountedModifierIds: string[]
  instoreModifierDiscountIds: string[]
  onlineDiscountedProductIds: string[]
  onlineRequiredProductIds: string[]
  onlineDiscountedModifierIds: string[]
  onlineModifierDiscountIds: string[]
  // Advanced mode keeps modifier discounts as their own list; otherwise they
  // are saved as a copy of the modifier restrictions
  instoreModifierDiscountsAdvanced: boolean
  onlineModifierDiscountsAdvanced: boolean
  instoreRedemptionType: InstoreRedemptionType
  availableCustomerSupport: boolean
  hiddenMenuKey?: string[] | string
  hiddenMenuName?: string
  url?: string
  longDescription: string
  images: {
    [ValidationTypes.RedeemAdvertising]: ImagePickerValue | undefined
    [ValidationTypes.RedeemDetail]: ImagePickerValue | undefined
    [ValidationTypes.RedeemIndex]: ImagePickerValue | undefined
  }
  numberOfPoints?: number
  multiUseRewards: MultiUseRewardsSettings
  couponCodes: {
    generationType: CouponPoolGenerationType
    type: CouponPoolType
    staticCode: string
    uploadColumn: number | null
  }
  selectedCouponPools: any[]
  posFields: Record<string, PosFields>
}

export type PosFields = {
  instoreRedemptionType: InstoreRedemptionType | null
  productIds?: RedeemProductIds
  instoreDiscountedProductIds: string[]
  instoreRequiredProductIds: string[]
  instoreDiscountedModifierIds: string[]
  instoreModifierDiscountIds: string[]
  onlineDiscountedProductIds: string[]
  onlineRequiredProductIds: string[]
  onlineDiscountedModifierIds: string[]
  onlineModifierDiscountIds: string[]
  instoreModifierDiscountsAdvanced: boolean
  couponCodes: {
    generationType: CouponPoolGenerationType
    type: CouponPoolType
    staticCode: string
    uploadColumn: number | null
  }
  selectedCouponPools: any[]
}

export type ModelContext = {
  model: FormModel
  setModel: (f: FormModel) => void
  // Sets the model and autosaves it, for changes made outside a Formsy field
  commitModel?: (f: FormModel) => void
}

const FormModelContext = createContext<ModelContext | null>(null)

export const FormModelProvider = FormModelContext.Provider

export function useFormModel(): ModelContext {
  const modelContext = useContext(FormModelContext)

  if (modelContext === null) {
    const errorInfo = 'useFormModel must be used within a FormModelProvider'
    throw errorInfo
  }

  return modelContext
}

export const baseFields = [
  'typeItem',
  'name',
  'description',
  'longDescription',
  'multiUseRewards',
] as const

export const onlineFlags = ['availableOnline', 'availableInstore'] as const
export const couponFields = ['couponCodes', 'selectedCouponPools'] as const

export const templateConstraintFields = ['minimum', 'maximum'] as const

export const requiredProductFields = [
  'requiredProductIds',
  'instoreRequiredProductIds',
  'onlineRequiredProductIds',
] as const

export const discountedProductFields = [
  'discountedProductIds',
  'instoreDiscountedProductIds',
  'onlineDiscountedProductIds',
] as const

export const discountedModifierFields = [
  'discountedModifierIds',
  'instoreDiscountedModifierIds',
  'onlineDiscountedModifierIds',
] as const

// Modifiers discounted with the item under a *_plus_discounted_modifiers
// scope (product_ids.<venue>.discounted_modifiers)
export const modifierDiscountFields = [
  'instoreModifierDiscountIds',
  'onlineModifierDiscountIds',
  'instoreModifierDiscountsAdvanced',
  'onlineModifierDiscountsAdvanced',
] as const
